// @givmo/board — the board's review of a copy of the return, linked (CBG-YR5-REVIEW; frame p7's
// second card, and the backend's `board-return-review.md` §3, §4.1).
//
// The review is the board's OWN act — a motion with a recorded vote made in an open session, or a
// written consent waiting on signatures or signed by everyone — which an officer links to the copy.
// Board never reads the motion's words to decide anything: it quotes them, as the board's.
//
// ⚠ THE MEETING IS NAMED BY ITS OWN TITLE AND DAY, never "board meeting" (the contract's approval
// condition): a committee's meeting can be the one linked once meetings have bodies (cut yr6meet).
//
// The card's facts are read fresh by the backend: a re-recorded vote shows its current tally, and a
// consent stopped after it was linked says so. One live link per copy; removing it keeps a note.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Skeleton,
  toast,
} from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { Link } from '../../board-routing';
import { displayDate } from '../../lib/display';
import { DialogFrame, PartLabel } from '../conflicts/coi-parts';
import { consentStatusLabel } from '../consents/consents-decode';
import { keptTogether } from '../roster/officer-roles';
import {
  longDate,
  removedLinkLine,
  reviewName,
  type ReturnCopy,
  type ReviewChoice,
} from './return-decode';
import { servedSentence, useReviewCandidates, useSettleReturnAct } from './return-queries';
import { returnWords as w } from './return-words';

const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const LINK =
  'font-sans text-s14 font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const QUIET_BUTTON =
  'self-start font-sans text-s13 font-w600 text-text-tertiary outline-none transition-colors hover:text-text-strong focus-visible:underline';

export function ReviewCard({
  orgRef,
  copy,
  canLink,
}: {
  orgRef: string;
  copy: ReturnCopy;
  canLink: boolean;
}) {
  const [opened, setOpened] = useState<'pick' | 'remove' | null>(null);
  const review = copy.review;
  const removed = copy.review_history
    .map(removedLinkLine)
    .filter((line): line is string => line !== null);

  return (
    <Card testId="return-review">
      <CardBody className="flex flex-col gap-12">
        <PartLabel>{w.theBoardsReview}</PartLabel>
        {review === null ? (
          <div className="flex flex-col items-start gap-12">
            <p className={PLAIN} data-testid="return-review-none">
              {w.noReviewRecorded}
            </p>
            {canLink ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setOpened('pick')}
                testId="return-review-record"
              >
                {w.recordReview}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-10" data-testid="return-review-linked">
            {review.motion !== null ? (
              <>
                <Link
                  to="/meetings/$meetingId/minutes"
                  params={{ meetingId: review.motion.meeting_id }}
                  className={LINK}
                  data-testid="return-review-meeting"
                >
                  {reviewName(review)}
                </Link>
                {review.motion.motion_text.length > 0 ? (
                  <blockquote
                    className={`border-l-2 border-text-strong pl-12 ${PLAIN} text-text-strong`}
                    data-testid="return-review-motion"
                  >
                    “{review.motion.motion_text}”
                  </blockquote>
                ) : null}
                {/* CBG-FIX92: a tally never breaks after its en dash ("Carried 3–" / "0"). */}
                <p className={`${PLAIN} whitespace-nowrap`} data-testid="return-review-tally">
                  {w.tally(
                    review.motion.outcome,
                    review.motion.votes_for,
                    review.motion.votes_against,
                    review.motion.votes_abstaining,
                  )}
                </p>
                {review.motion.minutes_approved_on !== null ? (
                  <p className={QUIET} data-testid="return-review-minutes">
                    {w.minutesApproved(longDate(review.motion.minutes_approved_on))}
                  </p>
                ) : null}
              </>
            ) : review.written_consent !== null ? (
              <>
                <Link
                  to="/consents/$consentId"
                  params={{ consentId: review.written_consent.written_consent_id }}
                  className={LINK}
                  data-testid="return-review-consent"
                >
                  {reviewName(review)}
                </Link>
                <p className={PLAIN} data-testid="return-review-consent-facts">
                  {[
                    review.written_consent.consent_date === null
                      ? null
                      : w.consentDated(longDate(review.written_consent.consent_date)),
                    consentStatusLabel(review.written_consent.status),
                  ]
                    .filter((part): part is string => part !== null)
                    .join(' · ')}
                </p>
              </>
            ) : null}
            {canLink ? (
              <button
                type="button"
                className={QUIET_BUTTON}
                onClick={() => setOpened('remove')}
                data-testid="return-review-remove"
              >
                {w.remove}
              </button>
            ) : null}
          </div>
        )}
        {removed.length > 0 ? (
          <ul
            className="flex flex-col gap-4 border-t border-border-subtle pt-10"
            data-testid="return-review-removed"
          >
            {removed.map((line, index) => (
              <li key={index} className={QUIET}>
                {line}
              </li>
            ))}
          </ul>
        ) : null}
      </CardBody>
      {opened === 'pick' ? (
        <ReviewPickerDialog orgRef={orgRef} copy={copy} onClose={() => setOpened(null)} />
      ) : null}
      {opened === 'remove' && review !== null ? (
        <RemoveReviewDialog
          orgRef={orgRef}
          copy={copy}
          linkId={review.link_id}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </Card>
  );
}

/** A choice's two lines: the act by its own name and day, then its words or its state. */
function choiceLines(choice: ReviewChoice): { label: string; description: string } {
  const day = choice.on === null ? null : displayDate(choice.on);
  if (choice.kind === 'motion') {
    const title = choice.title ?? '';
    // CBG-FIX92: the tally stays on one line inside the description, which is a string here, so
    // `keptTogether` (no-break spaces, a word joiner after the en dash), not a no-wrap span.
    const tally =
      choice.outcome === null
        ? null
        : keptTogether(
            w.tally(
              choice.outcome,
              choice.votes_for ?? 0,
              choice.votes_against ?? 0,
              choice.votes_abstaining ?? 0,
            ),
          );
    return {
      label: day === null ? title : w.meetingAndDay(title, day),
      description: [choice.detail === null ? null : `“${choice.detail}”`, tally]
        .filter((part): part is string => part !== null)
        .join(' · '),
    };
  }
  return {
    label: choice.title ?? w.untitledConsent,
    description: [
      day === null ? null : w.consentDated(day),
      choice.consent_status === null ? null : consentStatusLabel(choice.consent_status),
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
  };
}

function ReviewPickerDialog({
  orgRef,
  copy,
  onClose,
}: {
  orgRef: string;
  copy: ReturnCopy;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const settle = useSettleReturnAct(orgRef);
  const candidatesQuery = useReviewCandidates(orgRef, true);
  const choices = candidatesQuery.data ?? [];
  const motions = choices.filter((choice) => choice.kind === 'motion');
  const consents = choices.filter((choice) => choice.kind === 'written_consent');
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const link = useMutation({
    mutationFn: async () => {
      const chosen = choices.find((choice) => `${choice.kind}:${choice.id}` === value);
      if (chosen === undefined) return null;
      const { data } = await operations.officerLinkBoardReturnReview({
        path: { org_ref: orgRef, record_id: copy.record_id },
        body: { kind: chosen.kind, id: chosen.id },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (data) => {
      await settle(data);
      toast.success(w.reviewRecorded);
      onClose();
    },
    onError: (caught) => setError(servedSentence(caught, w.reviewFailed)),
  });

  return (
    <DialogFrame
      title={w.recordReview}
      lead={w.reviewPickerLead}
      testId="return-review-picker"
      width={520}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="return-review-picker-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={value === null}
            loading={link.isPending}
            onClick={() => link.mutate()}
            testId="return-review-picker-confirm"
          >
            {w.recordIt}
          </Button>
        </>
      }
    >
      {candidatesQuery.isPending ? (
        <Skeleton className="h-[96px] w-full rounded-lg" testId="return-review-picker-loading" />
      ) : candidatesQuery.isError ? (
        <ErrorMessage testId="return-review-picker-failed">{w.candidatesFailed}</ErrorMessage>
      ) : choices.length === 0 ? (
        <p className={PLAIN} data-testid="return-review-picker-none">
          {w.noCandidates}
        </p>
      ) : (
        <RadioTileGroup
          value={value ?? ''}
          onValueChange={(next) => setValue(next)}
          aria-label={w.recordReview}
          testId="return-review-picker-choices"
        >
          {motions.length > 0 ? <PartLabel>{w.motionsHeading}</PartLabel> : null}
          {motions.map((choice) => (
            <RadioTile
              key={choice.id}
              value={`${choice.kind}:${choice.id}`}
              {...choiceLines(choice)}
              testId={`return-review-choice-${choice.id}`}
            />
          ))}
          {consents.length > 0 ? <PartLabel>{w.consentsHeading}</PartLabel> : null}
          {consents.map((choice) => (
            <RadioTile
              key={choice.id}
              value={`${choice.kind}:${choice.id}`}
              {...choiceLines(choice)}
              testId={`return-review-choice-${choice.id}`}
            />
          ))}
        </RadioTileGroup>
      )}
      {error !== null ? (
        <ErrorMessage testId="return-review-picker-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

function RemoveReviewDialog({
  orgRef,
  copy,
  linkId,
  onClose,
}: {
  orgRef: string;
  copy: ReturnCopy;
  linkId: string;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const settle = useSettleReturnAct(orgRef);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async () => {
      // Named by the link it saw, so a stale page can never remove a link made since.
      const { data } = await operations.officerUnlinkBoardReturnReview({
        path: { org_ref: orgRef, record_id: copy.record_id, link_id: linkId },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (data) => {
      await settle(data);
      toast.success(w.removed);
      onClose();
    },
    onError: (caught) => setError(servedSentence(caught, w.removeFailed)),
  });

  return (
    <DialogFrame
      title={w.removeTitle}
      testId="return-review-remove-dialog"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="return-review-remove-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            testId="return-review-remove-confirm"
          >
            {w.remove}
          </Button>
        </>
      }
    >
      <p className={PLAIN}>{w.removeLead}</p>
      {error !== null ? (
        <ErrorMessage testId="return-review-remove-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}
